import React from 'react'
import styled from 'styled-components'

import { mapTheme } from 'etymos'
import theme from '../../theme'
import isCSSColor from '../../utils/isCSSColor'

/*
type Props = {
  variant?: 'display1' | 'display2' | 'h1' | 'h2' | 'h3' | 'h4' | 'subtitle1' | 'subtitle2' | 'body1' | 'body2' | 'caption1' | 'caption2' | 'code',
  wrap?: boolean,
  maxWrapLines?: number,
  align?: 'left' | 'right' | 'center' | 'start' | 'end' | 'justify',
  maxWidth?: string,
  interactive?: 'underline_default' | 'underline_hover' | 'no_underline',
}
 */

const variantToHtmlTag = {
	display1: 'h1',
	display2: 'h2',
	h1: 'h1',
	h2: 'h2',
	h3: 'h3',
	h4: 'h4',
	subtitle1: 'h5',
	subtitle2: 'h6',
	body1: 'p',
	body2: 'p',
	caption1: 'label',
	caption2: 'label',
	code: 'code',
}

const RawTextStyled = styled.div`
	padding: 0px;
	margin: 0px;

	${mapTheme((breakpoint, props) => {
		if (!breakpoint.isNew) return ''
		// Returns the base CSS
		return theme.typography.scale[props.$variant][breakpoint.textStyles]
	})}

	${props =>
		props.$wrap &&
		`
  white-space: normal;
  word-wrap: break-word;
  `};
	${props =>
		!props.$wrap &&
		`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  `};

	${props =>
		props.$variant === 'code' &&
		`
  	white-space: nowrap;
  `}

	${props =>
		/^h/.test(props.$variant) &&
		`
    margin-bottom: 0.75em;
  `}

	${props =>
		props.$maxWrapLines > 1 &&
		`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-box-align: start !important;
  -webkit-line-clamp: ${props.$maxWrapLines};
  overflow: hidden;
  text-overflow: ellipsis;
  `};

	${props =>
		props.$interactive &&
		`
  	cursor: pointer;
  `}

	${props =>
		props.$interactive === 'underline_default' &&
		`
  	text-decoration: underline;

  	&:hover {
  		text-decoration: none;
  	}
  `}

  ${props =>
		props.$interactive === 'underline_hover' &&
		`
  	text-decoration: none;

  	&:hover {
  		text-decoration: underline;
  	}
  `}

  ${props => props.$align && `text-align: ${props.$align};`}
  ${props => props.$maxWidth && `max-width: ${props.$maxWidth};`}

  ${props =>
		props.color && isCSSColor(props.color)
			? `color: ${props.color} !important;`
			: `color: var(--color-${props.color}) !important;`}
  ${props =>
		props.$color && isCSSColor(props.$color)
			? `color: ${props.$color} !important;`
			: `color: var(--color-${props.$color}) !important;`}
  ${props =>
		props.background && isCSSColor(props.background)
			? `background: ${props.background} !important;`
			: `background: var(--color-${props.background}) !important;`}
  ${p => p.weight && `font-weight: ${p.weight} !important;`}
	${p => p.lineHeight && `line-height: ${p.lineHeight} !important;`}
	${p => p.transform && `text-transform: ${p.transform} !important;`}
	${p => p.letterSpacing && `letter-spacing: ${p.letterSpacing} !important;`}
	${p => p.decoration && `text-decoration: ${p.decoration} !important;`}
`

const Text = props => {
	const { variant = 'body1', wrap = true, maxWrapLines, align = 'left', maxWidth, interactive, ...otherProps } = props

	const as = variantToHtmlTag[variant]

	return (
		<RawTextStyled
			as={as}
			$variant={variant}
			$wrap={wrap}
			$maxWrapLines={maxWrapLines}
			$align={align}
			$maxWidth={maxWidth}
			$interactive={interactive}
			{...otherProps}
			data-variant={variant}
		/>
	)
}

export const Display1 = props => <Text {...props} variant='display1' />
export const Display2 = props => <Text {...props} variant='display2' />
export const H1 = props => <Text {...props} variant='h1' />
export const H2 = props => <Text {...props} variant='h2' />
export const H3 = props => <Text {...props} variant='h3' />
export const H4 = props => <Text {...props} variant='h4' />
export const Subtitle1 = props => <Text {...props} variant='subtitle1' />
export const Subtitle2 = props => <Text {...props} variant='subtitle2' />
export const Body1 = props => <Text {...props} variant='body1' />
export const Body2 = props => <Text {...props} variant='body2' />
export const Caption1 = props => <Text {...props} variant='caption1' />
export const Caption2 = props => <Text {...props} variant='caption2' />
export const Code = props => <Text {...props} variant='code' />

export default Text
